import { motion } from "framer-motion";
import { Minus, Plus, ShieldCheck, Truck, Box, Check, Zap, Loader2 } from "lucide-react";
import { VARIANTS, KEY_BENEFITS, ADDONS, formatPrice, PRODUCT_NAME } from "@/data/product";

const reveal = {
    initial: { opacity: 0, y: 48 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function ProductSection({ variantId, setVariantId, quantity, setQuantity, bundle, setBundle, buying, onBuy }) {
    const variant = VARIANTS.find((v) => v.id === variantId);
    const addon = ADDONS[0];
    const total = variant.price * quantity + (bundle ? addon.price : 0);

    return (
        <section id="shop" data-testid="product-section" className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36">
            <motion.p {...reveal} className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-3">The Fan</motion.p>
            <motion.h2 {...reveal} className="font-display uppercase font-700 tracking-tighter text-4xl md:text-6xl mb-14">
                Choose your <span className="text-cyan-300">cold.</span>
            </motion.h2>

            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                <div className="lg:col-span-7 space-y-8">
                    {VARIANTS.map((v, idx) => (
                        <motion.button
                            key={v.id}
                            {...reveal}
                            transition={{ ...reveal.transition, delay: (idx % 2) * 0.08 }}
                            onClick={() => setVariantId(v.id)}
                            data-testid={`gallery-image-${v.id}`}
                            className={`relative block w-full text-left rounded-[1.75rem] overflow-hidden border transition-[border-color,transform] duration-500 hover:-translate-y-1 ${
                                variantId === v.id ? "border-cyan-300/60" : "border-white/10 hover:border-white/25"
                            }`}
                        >
                            <img src={v.image} alt={`${PRODUCT_NAME} — ${v.name} ${v.edition}`} className="w-full h-auto object-cover" loading={idx === 0 ? "eager" : "lazy"} />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 flex items-end justify-between">
                                <div>
                                    <p className="font-display uppercase text-xl md:text-2xl tracking-tight">{v.name}</p>
                                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200/80 mt-1">{v.edition}</p>
                                </div>
                                <p className="font-display text-xl text-white/90">{formatPrice(v.price)}</p>
                            </div>
                            {variantId === v.id && (
                                <span className="absolute top-4 right-4 rounded-full bg-cyan-300 text-black p-1.5">
                                    <Check className="w-4 h-4" />
                                </span>
                            )}
                        </motion.button>
                    ))}
                </div>

                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-24 rounded-[1.75rem] border border-white/10 bg-white/[0.03] backdrop-blur-xl p-7 md:p-9" data-testid="buy-panel">
                        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">{PRODUCT_NAME}</p>
                        <h3 className="font-display uppercase font-600 tracking-tight text-2xl md:text-3xl mt-2" data-testid="buy-panel-variant-name">
                            {variant.name} <span className="text-zinc-500">— {variant.edition}</span>
                        </h3>
                        <div className="flex items-baseline gap-3 mt-4">
                            <p className="font-display text-4xl text-cyan-300" data-testid="buy-panel-price">{formatPrice(variant.price)}</p>
                            <p className="text-xs text-zinc-500 uppercase tracking-[0.15em]">Free standard shipping</p>
                        </div>

                        <ul className="mt-7 space-y-3">
                            {KEY_BENEFITS.map((b) => (
                                <li key={b} className="flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
                                    <Check className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" /> {b}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-8">
                            <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Color / Edition</p>
                            <div className="flex gap-3" data-testid="variant-selector">
                                {VARIANTS.map((v) => (
                                    <button
                                        key={v.id}
                                        onClick={() => setVariantId(v.id)}
                                        data-testid={`variant-option-${v.id}`}
                                        aria-label={`${v.name} ${v.edition}`}
                                        className={`w-11 h-11 rounded-full border-2 transition-transform duration-300 hover:scale-110 ${
                                            variantId === v.id ? "border-cyan-300 scale-110" : "border-white/20"
                                        }`}
                                        style={{ backgroundColor: v.swatch }}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="mt-7 flex items-center gap-5">
                            <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">Qty</p>
                            <div className="flex items-center rounded-full border border-white/15" data-testid="quantity-selector">
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    data-testid="quantity-decrease"
                                    className="p-3 text-zinc-400 hover:text-white transition-colors"
                                    aria-label="Decrease quantity"
                                >
                                    <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-8 text-center font-display text-lg" data-testid="quantity-value">{quantity}</span>
                                <button
                                    onClick={() => setQuantity(Math.min(10, quantity + 1))}
                                    data-testid="quantity-increase"
                                    className="p-3 text-zinc-400 hover:text-white transition-colors"
                                    aria-label="Increase quantity"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => setBundle(!bundle)}
                            data-testid="buy-panel-bundle-toggle"
                            className={`mt-7 w-full flex items-center gap-3 rounded-2xl border p-3.5 text-left transition-colors duration-300 ${
                                bundle ? "border-cyan-300/60 bg-cyan-300/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"
                            }`}
                        >
                            <Zap className={`w-4 h-4 shrink-0 ${bundle ? "text-cyan-300" : "text-zinc-500"}`} />
                            <span className="flex-1 min-w-0">
                                <span className="block text-sm font-semibold">{addon.name}</span>
                                <span className="block text-xs text-zinc-500 truncate">{addon.desc}</span>
                            </span>
                            <span className={`text-sm font-display ${bundle ? "text-cyan-300" : "text-zinc-400"}`}>+{formatPrice(addon.price)}</span>
                        </button>

                        <button
                            onClick={onBuy}
                            disabled={buying}
                            data-testid="buy-panel-buy-button"
                            className="glow-btn mt-5 w-full rounded-full bg-cyan-300 text-black font-bold uppercase tracking-[0.15em] text-sm py-4 transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60 disabled:scale-100 flex items-center justify-center gap-2"
                        >
                            {buying && <Loader2 className="w-4 h-4 animate-spin" />}
                            {buying ? "Redirecting to secure checkout…" : `Buy Now — ${formatPrice(total)}`}
                        </button>
                        <p className="mt-3 text-center text-[11px] text-zinc-600 flex items-center justify-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-cyan-300/70" /> Payment handled securely by Shopify — we never see your card
                        </p>

                        <div className="mt-6 grid grid-cols-3 gap-2 text-center" data-testid="buy-panel-trust">
                            {[
                                { icon: ShieldCheck, label: "Secure checkout" },
                                { icon: Truck, label: "Ships in 24–48h" },
                                { icon: Box, label: "Cable + lanyard included" },
                            ].map(({ icon: Icon, label }) => (
                                <div key={label} className="rounded-xl border border-white/10 px-2 py-3">
                                    <Icon className="w-4 h-4 text-cyan-300 mx-auto mb-1.5" />
                                    <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 leading-tight">{label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
